import { bridge } from "@dinahmoe/dmafjs-core";

import { visualEntities } from "../entities.js";
import { create } from "../helpers/elements.helper.js";
import { cssValue } from "../helpers/styles.helper.js";
import { VisualSuperHtml } from "./visual-super.environment.js";
import { TextHtml } from "./text.environment.js";

class InputHtml extends TextHtml {
  handlers = this.extendParentHandlers({
    "text.content": this.setContent,
    "input.readonly": this.setReadonly,
    "input.autoComplete": this.setAutoComplete,
    "input.placeholder": this.setPlaceholder,
    "text.userSelect": this.setUserSelect,
  });

  setProps(eNode) {
    const inputTagName = eNode.props["input.textarea"] ? "textarea" : "input";
    eNode.element = create(eNode, inputTagName);

    eNode.element.style.pointerEvents = "auto";
    eNode.element.style.backgroundColor = "transparent";
    eNode.element.style.outline = "none";
    eNode.element.style.border = "0";

    if (eNode.props["input.textarea"]) {
      eNode.element.setAttribute("rows", "1");
    } else {
      eNode.element.type = eNode.props["input.type"] ?? "text";
    }

    // Skip TextHtml.setProps (which creates a span) — call VisualSuperHtml directly
    VisualSuperHtml.prototype.setProps.call(this, eNode);

    if (!eNode.props["input.readonly"]) {
      eNode.onInput = () => this.onInputHandler(eNode);
      eNode.element.addEventListener("input", eNode.onInput);
    }

    if (eNode.props["input.autoResize"]) {
      requestAnimationFrame(() => {
        this.setDynamicHeight(eNode);
      });
    }

    // autofocus only works onLoad
    // element must be mounted before setting focus
    if (eNode.props["input.autoFocus"] !== undefined) {
      setTimeout(() => {
        eNode.element.focus();
      }, 0);
    }

    if (eNode.props["input.autoComplete"] !== undefined) {
      const autocompleteValue = this.getAutocompleteValue(eNode);
      eNode.element.setAttribute("autocomplete", autocompleteValue);
    }
  }

  setContent(eNode) {
    eNode.element.value = eNode.props["text.content"];
  }

  setType(eNode) {
    eNode.element.setAttribute("type", eNode.props["input.type"]);
  }

  setReadonly(eNode) {
    if (!eNode.props["input.readonly"]) {
      eNode.element.removeAttribute("readonly");
      return;
    }

    eNode.element.setAttribute("readonly", true);
  }

  setAutoComplete(eNode) {
    if (eNode.props["input.autoComplete"] !== undefined) {
      const autocompleteValue = this.getAutocompleteValue(eNode);
      eNode.element.setAttribute("autocomplete", autocompleteValue);
    }
  }

  getAutocompleteValue(eNode) {
    // If autocomplete is explicitly disabled, return "off"
    if (eNode.props["input.autoComplete"] === false) {
      return "off";
    }

    // If autocomplete is enabled, use type-specific values for better browser recognition
    if (eNode.props["input.autoComplete"] === true) {
      switch (eNode.props["input.type"]) {
        case "email":
          return "email";
        case "password":
          return "current-password";
        default:
          return "on";
      }
    }

    return "off";
  }

  setAutoFocus(eNode) {
    if (eNode.props["input.autoFocus"] !== undefined) {
      console.error("autoFocus", eNode.element);
      //eNode.element.setAttribute("autofocus", eNode.props["input.autoFocus"]);
      eNode.element.focus();
    }
  }

  setPlaceholder(eNode) {
    if (eNode.props["input.placeholder"]) {
      eNode.element.setAttribute("placeholder", eNode.props["input.placeholder"]);
    }
  }

  setUserSelect(eNode) {
    if (!eNode.props["text.userSelect"]) return;
    eNode.element.tabIndex = eNode.props["text.userSelect"] === "none" ? -1 : 0;
  }

  setDynamicHeight(eNode) {
    eNode.element.style.height = "auto";
    let newHeight = eNode.element.scrollHeight;

    if (eNode.props["size.heightMin"] !== undefined) {
      newHeight = Math.max(newHeight, eNode.props["size.heightMin"]);
    }
    if (eNode.props["size.heightMax"] !== undefined) {
      newHeight = Math.min(newHeight, eNode.props["size.heightMax"]);
    }

    if (newHeight && newHeight !== eNode.props["size.height"]) {
      bridge.update({
        uid: eNode.uid,
        key: "height_updated",
        value: {
          id: eNode.props["id"],
          height: newHeight,
        },
        sender: "browser",
      });
    } else {
      eNode.element.style.height = cssValue(eNode.props["size.height"]);
    }
  }

  destroy(eNode) {
    eNode.element.removeEventListener("input", eNode.onInput);
  }

  onInputHandler(eNode) {
    let inputValue = eNode.element.value;

    if (eNode.props["input.maxLength"]) {
      inputValue = inputValue.slice(0, eNode.props["input.maxLength"]);
    }

    if (eNode.props["text.toUpperCase"]) {
      inputValue = inputValue.toUpperCase();
    }

    if (eNode.props["text.toLowerCase"]) {
      inputValue = inputValue.toLowerCase();
    }

    let isValid = true;
    if (eNode.props["input.type"] === "email") {
      const emailPattern = /[\d%+._a-z-]+@[\d.a-z-]+\.[a-z]{2,4}$/;
      isValid = emailPattern.test(inputValue);
    }

    eNode.element.value = inputValue;
    const content = eNode.props["input.type"] === "number" ? Number(inputValue) : inputValue;

    if (eNode.props["input.autoResize"]) {
      this.setDynamicHeight(eNode);
    }

    bridge.update({
      uid: eNode.uid,
      key: "content_updated",
      value: {
        id: eNode.props["id"],
        content,
        isValid,
      },
      sender: "browser",
    });
  }
}

const inputHtml = new InputHtml("visual:text:input");
visualEntities.set("visual:text:input", inputHtml);
export { InputHtml, inputHtml };
